import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { siteMeta } from "../siteData";
import { useLanguage } from "../i18n";

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState(null);

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (!form.name || !form.email || !form.message) {
      setStatus(t("contact.missingFields"));
      return;
    }

    const subject = encodeURIComponent(form.subject || `${t("contact.subject")}: ${form.name}`);
    const body = encodeURIComponent(
      `Name: ${form.name}
Email: ${form.email}
Phone: ${form.phone || "Not provided"}

${form.message}`
    );

    window.location.href = `${siteMeta.emailHref}?subject=${subject}&body=${body}`;
    setStatus(t("contact.emailClientOpened"));
  }

  return (
    <div className="font-sans-clean min-h-screen bg-[#FDF8F3]">
      <section className="relative py-20 sm:py-24 overflow-hidden bg-[#5b2229]">
        <div className="absolute inset-0 opacity-20 ethiopian-cross" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold text-white mb-4">
              {t("contact.title")}
            </h1>
            <p className="text-white/80 text-lg">{t("contact.intro")}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-2 gap-10">
        <div className="bg-white rounded-3xl shadow-lg p-8">
          <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37] mb-6">
            {t("contact.formTitle")}
          </h2>
          <p className="text-sm text-gray-600 mb-6">{t("contact.formIntro")}</p>

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("contact.name")}
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                placeholder={t("contact.namePlaceholder")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("contact.email")}
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  placeholder={t("contact.emailPlaceholder")}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("contact.phone")}
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  placeholder={t("contact.phonePlaceholder")}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("contact.subject")}
              </label>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                placeholder={t("contact.subjectPlaceholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("contact.message")}
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                placeholder={t("contact.messagePlaceholder")}
              />
            </div>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#722F37] text-white text-sm font-medium hover:bg-[#5A252C] transition-colors"
            >
              <Send className="w-4 h-4" />
              {t("contact.send")}
            </button>

            {status && <p className="mt-3 text-sm text-gray-600">{status}</p>}
          </form>
        </div>

        <div className="space-y-6">
          <div className="bg-[#722F37] text-white rounded-3xl p-8">
            <h2 className="font-serif-elegant text-2xl font-bold mb-4">
              {t("contact.visitTitle")}
            </h2>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-[#C9A227]" />
                <span>
                  {siteMeta.addressLine1}
                  <br />
                  {siteMeta.addressLine2}
                  <br />
                  {t("layout.meetingLocation", siteMeta.meetingLocation)}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#C9A227]" />
                <a href={siteMeta.phoneHref} className="hover:underline">
                  {siteMeta.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#C9A227]" />
                <a href={siteMeta.emailHref} className="hover:underline">
                  {siteMeta.email}
                </a>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-3xl p-6 shadow-sm">
            <h3 className="font-serif-elegant text-lg font-semibold text-[#722F37] mb-3">
              {t("contact.planningTitle")}
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {t("contact.planningBody")}
            </p>
            <a
              href={siteMeta.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex mt-4 text-sm font-medium text-[#722F37] hover:text-[#C9A227]"
            >
              {t("contact.directions")}
            </a>
          </div>

          <div className="bg-white rounded-3xl p-4 shadow-sm border border-[#C9A227]/10">
            <h3 className="font-serif-elegant text-lg font-semibold text-[#722F37] mb-3">
              {t("contact.mapTitle")}
            </h3>
            <div className="overflow-hidden rounded-2xl border border-[#C9A227]/10">
              <iframe
                title={t("contact.mapTitle")}
                src="https://www.google.com/maps?q=11%20N%207th%20Street%2C%20Madison%2C%20WI%2053704&z=15&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
